import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemeProvider as StyledThemeProvider, createGlobalStyle } from 'styled-components';
import { createTheme } from '../theme';

const ThemeContext = createContext();

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.foreground};
  }

  h1, h2, h3, h4, h5, h6 {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

function applyCssVariables(mode) {
  const theme = createTheme(mode);
  const root = document.documentElement;
  const c = theme.colors;

  root.style.setProperty('--background', c.background);
  root.style.setProperty('--foreground', c.foreground);
  root.style.setProperty('--card', c.card);
  root.style.setProperty('--border', c.border);
  root.style.setProperty('--muted-foreground', c.mutedForeground);
  root.style.setProperty('--ring', c.ring);
}

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored === 'dark' || stored === 'light') return stored;
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('theme', themeMode);
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(themeMode);
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(themeMode);
    applyCssVariables(themeMode);
  }, [themeMode]);

  const toggleTheme = () => {
    setThemeMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const setTheme = (newTheme) => {
    setThemeMode(newTheme);
  };

  const theme = createTheme(themeMode);

  return (
    <ThemeContext.Provider value={{ theme: themeMode, toggleTheme, setTheme }}>
      <StyledThemeProvider theme={theme}>
        <GlobalStyle />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};
